import { useMemo, type ReactNode } from "react";
import { AlertTriangle, Loader2, SearchX } from "lucide-react";

import { useT } from "../../i18n";
import { recordMatches } from "../../lib/filterMatch";
import { useDataStore } from "../../store/dataStore";
import { useFiltersStore } from "../../store/filtersStore";
import { useLayersStore } from "../../store/layersStore";

function Card({ children }: { children: ReactNode }) {
  return (
    <div data-map-overlay="true" className="pointer-events-auto max-w-xs rounded-panel border border-border-subtle bg-surface-overlay/95 p-4 text-center shadow-lg backdrop-blur">
      {children}
    </div>
  );
}

/**
 * Loading / empty / error feedback over the map (brief §13). Empty isn't just
 * "no data" — it explains why and offers the fix (reset filters), the biggest
 * source of confusion in a filtered view.
 */
export function MapStates() {
  const t = useT();
  const status = useDataStore((s) => s.status);
  const records = useDataStore((s) => s.records);
  const applied = useFiltersStore((s) => s.applied);
  const grades = useLayersStore((s) => s.grades);
  const resetFilters = useFiltersStore((s) => s.reset);
  const clearGrades = useLayersStore((s) => s.clearGrades);

  const matched = useMemo(
    () => records.reduce((n, r) => n + (recordMatches(r, applied, grades) ? 1 : 0), 0),
    [records, applied, grades],
  );

  let content: ReactNode = null;

  if (status === "loading") {
    content = (
      <div data-map-overlay="true" className="pointer-events-auto flex items-center gap-2 rounded-full border border-border-subtle bg-surface-overlay/90 px-3 py-1.5 text-sm text-text-secondary shadow-md backdrop-blur">
        <Loader2 size={16} className="animate-spin text-accent" />
        {t.states.loading}
      </div>
    );
  } else if (status === "error") {
    content = (
      <Card>
        <AlertTriangle size={22} className="mx-auto text-status-critical" />
        <h2 className="mt-2 text-sm font-semibold text-text-primary">{t.states.errorTitle}</h2>
        <p className="mt-1 text-xs text-text-secondary">{t.states.errorBody}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-3 rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-text-onAccent transition-colors hover:bg-accent-hover"
        >
          {t.states.errorRetry}
        </button>
      </Card>
    );
  } else if (records.length > 0 && matched === 0) {
    content = (
      <Card>
        <SearchX size={22} className="mx-auto text-text-tertiary" />
        <h2 className="mt-2 text-sm font-semibold text-text-primary">{t.states.emptyTitle}</h2>
        <p className="mt-1 text-xs text-text-secondary">{t.states.emptyBody}</p>
        <button
          type="button"
          onClick={() => {
            resetFilters();
            clearGrades();
          }}
          className="mt-3 rounded-lg border border-border-default px-3 py-1.5 text-sm text-text-secondary transition-colors hover:border-accent-border hover:text-accent"
        >
          {t.states.emptyReset}
        </button>
      </Card>
    );
  }

  if (!content) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none absolute inset-0 z-[15] flex items-center justify-center p-4"
    >
      {content}
    </div>
  );
}
